import { PencilIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Modal } from '../ui';
import type { Service } from '../../stores/servicesStore';

export interface ServiceViewModalProps {
	service: Service | null;
	onClose: () => void;
	/** Shows the "Edit Service" action. Omit for view-only viewers. */
	onEdit?: (service: Service) => void;
}

/**
 * Read-only detail of a service and its pricing combinations. Shared by the
 * Provider Settings → Services tab and the Staff Services page; for staff with
 * SERVICE_VIEW but not SERVICE_MANAGE this is the whole of what they can reach.
 */
const ServiceViewModal = ({ service, onClose, onEdit }: ServiceViewModalProps) => (
	<Modal
		isOpen={!!service}
		onClose={onClose}
		title={service?.name || 'Service Details'}
		size="lg"
		bodyClassName="max-h-[60vh] overflow-y-auto"
	>
		{service && (
			<div className="space-y-5">
				<div className="flex items-center gap-3">
					<Badge variant={service.status === 'ACTIVE' ? 'success' : 'warning'}>
						{service.status}
					</Badge>
				</div>

				{service.description && (
					<div>
						<p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
							Description
						</p>
						<p className="text-sm text-gray-700 dark:text-gray-300">
							{service.description}
						</p>
					</div>
				)}

				<div>
					<p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
						Pricing Combinations ({service.variants.length})
					</p>
					{service.variants.length === 0 ? (
						<p className="text-sm text-gray-400 dark:text-gray-500 italic">
							No pricing combinations added.
						</p>
					) : (
						<div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
							<table className="w-full text-sm">
								<thead>
									<tr className="bg-gray-50 dark:bg-gray-700/50">
										<th className="text-left px-4 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase">
											Size
										</th>
										<th className="text-left px-4 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase">
											Length
										</th>
										<th className="text-right px-4 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase">
											Duration
										</th>
										<th className="text-right px-4 py-2.5 font-semibold text-gray-600 dark:text-gray-300 text-xs uppercase">
											Price
										</th>
									</tr>
								</thead>
								<tbody>
									{service.variants.map((v, i) => (
										<tr
											key={v.id || i}
											className="border-t border-gray-100 dark:border-gray-700"
										>
											<td className="px-4 py-2.5 text-gray-900 dark:text-white">
												{v.size}
											</td>
											<td className="px-4 py-2.5 text-gray-900 dark:text-white">
												{v.length}
											</td>
											<td className="px-4 py-2.5 text-right text-gray-600 dark:text-gray-300">
												{v.duration} min
											</td>
											<td className="px-4 py-2.5 text-right font-semibold text-gray-900 dark:text-white">
												${v.price.toFixed(2)}
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</div>

				<div className="flex gap-3 pt-2">
					{onEdit && (
						<Button
							variant="primary"
							className="flex-1"
							onClick={() => onEdit(service)}
						>
							<PencilIcon className="w-4 h-4" />
							Edit Service
						</Button>
					)}
					<Button
						variant="secondary"
						className={onEdit ? undefined : 'flex-1'}
						onClick={onClose}
					>
						Close
					</Button>
				</div>
			</div>
		)}
	</Modal>
);

export default ServiceViewModal;
